import { useEffect, useState } from 'react'
import * as m from '@/paraglide/messages'

/**
 * Gebrandeter Vollbild-Wartezustand (30.07.2026).
 *
 * Vorher stand an vier Stellen derselbe nackte Rand-Spinner: Auth-Callback,
 * Startseiten-Weiterleitung und die Sitzungs-Auffrischung im Login (die
 * Einladungsseite behaelt ihren eigenen, sie ist ein Zustand INNERHALB einer
 * Seite, kein Vollbild). Auf dem Callback-Schirm kam zusaetzlich eine
 * Entwickler-Zeile ("code=yes, hash_token=no, invite=no") ins Bild.
 *
 * Diese Komponente ersetzt alle drei (die Einzel-Spinner sind geloescht, nicht
 * ergaenzt) und bringt drei Dinge mit, die vorher fehlten:
 *   - Jinx als Wartefigur mit Sprechblase statt eines rotierenden Rands.
 *   - Eine erkennbare Fortschritts-Anzeige: zwei gegenlaeufige Conic-Boegen um
 *     die Figur, dazu die versetzt pulsierenden Punkte in der Blase.
 *   - Einen AUSWEG. Der Callback drehte sich beim Betreiber ueber zwei Minuten
 *     ohne Erklaerung und ohne Weg zurueck. Nach SLOW_HINT_MS sagt die Figur,
 *     dass es laenger dauert, und bietet den Rueckweg als Schaltflaeche an.
 *
 * Das Chat-Widget (components/mascot/mascot-widget) bleibt auf dem Callback
 * ausgeblendet (__root.tsx) — zwei Jinx gleichzeitig, einer davon als
 * Support-Chat auf einem Ladeschirm, ergibt keinen Sinn.
 *
 * Bewegung: Keyframes in global.css (`loading-orbit`, `loading-dot`), dort
 * liegen auch die uebrigen ~25 der App. `prefers-reduced-motion` schaltet Ring
 * und Punkte still; das Jinx-Video laeuft weiter, es ist der Inhalt.
 */

/** Ab wann der Wartezustand zugibt, dass etwas klemmt (Betreiber-Vorgabe 30.07.). */
const SLOW_HINT_MS = 10_000

/** „Denkende" Variante aus dem bestehenden Jinx-Satz (public/mascot/). */
const THINK_SRC = '/mascot/think_720.webm'

const ACCENT = '#FF6B00'

/**
 * Schriftfarben fest, nicht aus dem Theme (30.07.2026).
 *
 * Der Schirm setzt seinen Hintergrund fest auf #0a0a0a — er ist bewusst immer
 * dunkel, egal welches Theme gilt. Die Schrift hing aber an `text-on-surface`,
 * und dieses Token ist im HELLEN Theme fast schwarz (#2a2420, global.css).
 * Ergebnis fuer jeden, der den Schirm ohne aktiven Dunkelmodus sah: dunkelgrau
 * auf fast schwarz, kaum lesbar. Der Betreiber meldete es am 30.07. von
 * preview.emonse.com — auf localhost fiel es nie auf, weil dort ein
 * Theme-Cookie vom letzten Login den Dunkelmodus hielt.
 *
 * Es sind exakt die Werte des DUNKLEN Themes; der Schirm sieht damit ueberall
 * so aus wie bisher im Dunkelmodus. Kein Widerspruch zu „Theme-Tokens statt
 * Festfarben": wo der Hintergrund fest ist, muss es der Vordergrund auch sein —
 * sonst haengt genau ein Teil am Theme und bricht mit ihm.
 */
const TEXT = '#dddad9'
const TEXT_MUTED = '#d4b5a5'

export function LoadingScreen({ message }: { message?: string }) {
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_HINT_MS)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-5">
      <div className="flex flex-col items-center text-center w-full max-w-[360px]">
        {/* Sprechblase */}
        <div className="relative w-full">
          <div
            className="relative rounded-2xl border border-white/[0.07] bg-linear-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-xl px-6 py-4 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.75)]"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center justify-center gap-2.5">
              <p className="text-[15px] font-medium leading-snug" style={{ color: TEXT }}>
                {message || m.loading_default()}
              </p>
              <span className="flex items-end gap-[3px] pb-[3px] shrink-0" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="loading-dot w-[5px] h-[5px] rounded-full"
                    style={{
                      background: ACCENT,
                      animation: `loading-dot 1.4s ease-in-out ${i * 0.16}s infinite`,
                    }}
                  />
                ))}
              </span>
            </div>
            {slow && (
              <p
                className="text-[12px] leading-relaxed mt-2.5 border-t border-white/[0.05] pt-2.5"
                style={{ color: `${TEXT_MUTED}8c` }}
              >
                {m.loading_taking_longer()}
              </p>
            )}
            {/* Zipfel zur Figur: gedrehtes Quadrat mit denselben zwei Kanten.
                Ohne eigenen backdrop-blur — der zweite Blur-Layer hellte die
                Flaeche gegen die Blase auf und der Zipfel las sich als
                aufgesetzte Raute statt als Fortsetzung (Eigenkritik 30.07.).
                Fuellung exakt der untere Ton des Blasenverlaufs. */}
            <div className="absolute left-1/2 -bottom-[5px] -translate-x-1/2 h-2.5 w-2.5 rotate-45 rounded-[2px] border-b border-r border-white/[0.07] bg-[rgb(23,23,23)]" />
          </div>
        </div>

        {/* Figur mit Fortschritts-Ringen */}
        <div className="relative w-[150px] h-[150px] mt-5">
          <div
            className="absolute inset-[-6%] rounded-full blur-2xl"
            style={{ background: `${ACCENT}1f` }}
            aria-hidden="true"
          />
          <div
            className="loading-ring absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(from 0deg, transparent 0deg, ${ACCENT}00 40deg, ${ACCENT}cc 120deg, transparent 165deg)`,
              animation: 'loading-orbit 3.2s linear infinite',
            }}
            aria-hidden="true"
          />
          {/* Gegenlaeufiger Innenring. Erst bei 0.55 Deckkraft ueberhaupt
              wahrnehmbar — bei 0.28 war er auf dem dunklen Grund unsichtbar
              und der Aufwand umsonst (Eigenkritik 30.07.). */}
          <div
            className="loading-ring absolute inset-[9%] rounded-full"
            style={{
              background:
                'conic-gradient(from 180deg, transparent 0deg, rgba(255,255,255,0.55) 75deg, transparent 120deg)',
              animation: 'loading-orbit-reverse 5.4s linear infinite',
            }}
            aria-hidden="true"
          />
          <video
            className="absolute inset-[12%] w-[76%] h-[76%] object-contain"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          >
            <source src={THINK_SRC} type="video/webm" />
          </video>
        </div>

        {/* Der Rueckweg erscheint erst mit dem Hinweis — vorher waere er nur ein
            Angebot, einen normalen Ladevorgang abzubrechen. */}
        {slow && (
          <a
            href="/login"
            className="group mt-6 inline-flex items-center gap-2 rounded-xl border border-white/[0.09] bg-white/[0.03] px-4 py-2.5 text-[13px] font-semibold transition-all hover:border-[#FF6B00]/45 hover:bg-[#FF6B00]/[0.08] hover:brightness-125"
            style={{ color: `${TEXT_MUTED}cc` }}
          >
            <span className="material-symbols-outlined text-[17px] transition-transform group-hover:-translate-x-0.5">
              arrow_back
            </span>
            {m.loading_back_to_login()}
          </a>
        )}
      </div>
    </div>
  )
}
